import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { getJsmPlaygroundSignupIntent } from '@/components/signup/utils/jsm-playground-signup-intent';
import { ProductKey } from '@/components/signup/utils/products';
import { CardsGrid } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/cards-grid.tsx';
import { JsmUseCaseCardsGrid } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/jsm-use-case-cards-grid.tsx';
import { experiments } from '@/utils/experiment/manifest';
import { getExperimentWithExposures, useExperiment } from '@/utils/experiment/use-experiment';

import { useIsJsmCrossflowConfirmation } from '../../hooks/use-is-jsm-crossflow-confirmation';
import { useWorkStyleChoiceTreatment } from '../../hooks/use-work-style-choice-treatment';
import { JiraOnboardingContainer } from '../jira/jira-onboarding-container/jira-onboarding-container';
import { JsmUseCaseOnboardingContainer } from '../jsm-use-case-onboarding/jsm-use-case-onboarding-container';
import { ServCoOnboardingContainer } from '../serv-co/serv-co-onboarding-container/serv-co-onboarding-container';
import { TwcOnboardingContainer } from '../twc-provisioning-screen/components/twc-onboarding-container';

type MessagesType = {
	readonly teamFocus: string;
	readonly personalizeJira: string;
};

const TeamFocusView = () => {
	const intl = useIntl();
	const searchParameters = useSearchParameters();
	const { products = [] } = searchParameters;
	const isJsmCrossflow = useIsJsmCrossflowConfirmation();
	const isPlaygroundSignup = useMemo(
		() => Boolean(getJsmPlaygroundSignupIntent(window.location.search)),
		[],
	);

	const isServCo = products.includes(ProductKey.SERVICE_COLLECTION);
	const isTwc = products.includes(ProductKey.TEAMWORK_COLLECTION);

	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmExperimentEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;
	const isJsm = products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT);

	// For JSM Land use-case onboarding experiment:
	// enabled (variant) users see JSM-specific card grid (6 upfront + expand).
	// disabled (control) users and non-enrolled users see the original grid.
	const shouldUseJsmUseCaseCards = isJsmExperimentEnabled || isPlaygroundSignup;
	const cardsGrid = shouldUseJsmUseCaseCards ? <JsmUseCaseCardsGrid /> : <CardsGrid />;
	const jsmUseCaseSubheader = intl.formatMessage({
		id: 'UserSeg.teamTypeSelection.jsmUseCase.subHeader',
		defaultMessage: "Choose the best fit for your team. We'll help you get started.",
	});

	const { isEligible: isWorkStyleChoiceEligible, isEnabled: isWorkStyleChoiceEnabled } =
		useWorkStyleChoiceTreatment();

	useEffect(() => {
		if (isWorkStyleChoiceEligible) {
			getExperimentWithExposures(experiments.jiraSignupWorkStyleChoice);
		}
	}, [isWorkStyleChoiceEligible]);

	if (isTwc) {
		return (
			<TwcOnboardingContainer
				header={intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.twc.jtbdD0onboarding.header',
					defaultMessage: 'What kind of work do you do?',
				})}
				subheader={intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.twc.jtbdD0onboarding.subHeader',
					defaultMessage: 'This helps us personalize your experience.',
				})}
			>
				<CardsGrid />
			</TwcOnboardingContainer>
		);
	}

	const jiraSubheader = isWorkStyleChoiceEnabled
		? intl.formatMessage({
				id: 'UserSeg.teamTypeSelection.workStyleChoice.subHeader',
				defaultMessage:
					'This helps us suggest features and integrations that help your team do their best work.',
			})
		: intl.formatMessage({
				id: 'UserSeg.teamTypeSelection.transparentTemplateForAll.subHeader',
				defaultMessage: 'This helps us suggest templates that help your team do their best work.',
			});

	const messages: MessagesType = {
		teamFocus: intl.formatMessage({
			id: 'UserSeg.teamTypeSelection.header',
			defaultMessage: 'What kind of work do you do?',
		}),
		personalizeJira: shouldUseJsmUseCaseCards ? jsmUseCaseSubheader : jiraSubheader,
	};

	const jiraOnboardingContainer = (
		<JiraOnboardingContainer header={messages.teamFocus} subheader={messages.personalizeJira}>
			{cardsGrid}
		</JiraOnboardingContainer>
	);

	if (!isJsm && !isServCo) {
		return jiraOnboardingContainer;
	}

	if (shouldUseJsmUseCaseCards) {
		return (
			<JsmUseCaseOnboardingContainer
				header={messages.teamFocus}
				subheader={messages.personalizeJira}
				isJsmCrossflow
			>
				{cardsGrid}
			</JsmUseCaseOnboardingContainer>
		);
	}

	return (
		<ServCoOnboardingContainer
			header={messages.teamFocus}
			subheader={messages.personalizeJira}
			isJsmCrossflow={isJsmCrossflow}
		>
			{cardsGrid}
		</ServCoOnboardingContainer>
	);
};

export default TeamFocusView;
